import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import i18next from 'i18next';
import dayjs from 'dayjs';

import {
  ReplyRounded,
  ReplyAllRounded,
  SortRounded,
  UnfoldLessRounded,
  UnfoldMoreRounded,
} from '@mui/icons-material';
import { Box, Chip, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import isToday from 'dayjs/plugin/isToday';
import 'dayjs/locale/es';
import 'dayjs/locale/en';

import { useDraft } from '@frontend/features/cases/context/DraftContext';
import { useEditor } from '@frontend/features/cases/hooks/useEditor';
import { QueryKeys } from '@frontend/features/common/types';
import { client } from '@frontend/features/common/services';
import { Channels } from '@bandido/shared/constants';

interface ThreadEmail {
  id: number;
  from: string;
  subject: string;
  original_content: string;
  original_text: string;
  content: string;
  created_at: string;
}

import AttachmentsList from '../../../AttachmentsList';
import { ContentStack, EditorContainer, ThreadToggleButton } from '../styled';
import { filesNotEmbedded, getReplyEmailBody, hasFullEmailContent } from '../emailDisplayContent';
import { MessageContentProps } from '../types';
import { HeaderRow } from './HeaderRow';
import { EmailThread } from './EmailThread';


dayjs.extend(isToday);
dayjs.locale(i18next.language);

export const MessageContent = ({
  history,
  isCurrentUser,
  createdAtTime,
  createdAtDate,
  handleEditorClickEvent,
}: MessageContentProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { removeDraft } = useDraft();
  const { isOpen, setIsOpen, setSubject, setChannel, setTo, setHistoryIdToReply, setCc, onToggleCc, setEditorContent } =
    useEditor();

  // Lo que se cita al responder: siempre el cuerpo limpio, sin importar el toggle.
  const replyBody = getReplyEmailBody(history);
  const [showFullEmail, setShowFullEmail] = useState(false);
  const canShowFullEmail = hasFullEmailContent(history);
  // Lo que se ve en pantalla: el limpio, o el completo si el agente lo pidió.
  const content = showFullEmail && canShowFullEmail ? history.emailContent : replyBody;
  const subject = history.noteSubject ?? history.emailSubject;
  const destinataries = history.emailTo?.join(', ');
  const cc = history.emailCC?.join(', ');
  const bcc = history.emailBCC?.join(', ');

  const fetchEmailThread = async (emailId: number): Promise<ThreadEmail[]> => {
    try {
      const response = await queryClient.fetchQuery({
        queryKey: [QueryKeys.getEmailThread, emailId],
        queryFn: () => client.get<ThreadEmail[]>(`emails/${emailId}/thread`),
        staleTime: 0,
      });
      return response.data || [];
    } catch (error) {
      console.error('Error loading thread for emailId:', emailId, error);
      return [];
    }
  };

  const buildThreadHistory = (emails: ThreadEmail[]) => {
    if (!emails || emails.length === 0) {
      const from = history.emailFrom;
      const date = dayjs(history.createdAt).format('ddd, D MMM YYYY');

      return `
        <blockquote>
          <div><strong>${t('cases.view.content.from')}:</strong> ${from}</div>
          <div><strong>${t('cases.view.content.date')}:</strong> ${date} ${createdAtTime}</div>
          <div><br/></div>
          ${replyBody}
        </blockquote>
      `;
    }

    const sortedEmails = [...emails].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    let threadHtml = '';

    for (let i = 0; i < sortedEmails.length; i++) {
      const email = sortedEmails[i];
      const emailDate = dayjs(email.created_at).format('ddd, D MMM YYYY HH:mm');
      const emailContent = email.original_content || email.original_text || email.content;

      if (i === 0) {
        threadHtml = `
          <blockquote>
            <div><strong>${t('cases.view.content.from')}:</strong> ${email.from}</div>
            <div><strong>${t('cases.view.content.date')}:</strong> ${emailDate}</div>
            ${email.subject ? `<div><strong>${t('cases.view.content.subject')}:</strong> ${email.subject}</div>` : ''}
            <div><br/></div>
            ${emailContent}
            <div><br/></div>
          </blockquote>
        `;
      } else {
        threadHtml = `
          <blockquote>
            <div><strong>${t('cases.view.content.from')}:</strong> ${email.from}</div>
            <div><strong>${t('cases.view.content.date')}:</strong> ${emailDate}</div>
            ${email.subject ? `<div><strong>${t('cases.view.content.subject')}:</strong> ${email.subject}</div>` : ''}
            <div><br/></div>
            ${emailContent}
            ${threadHtml}
            <div><br/></div>
          </blockquote>
        `;
      }
    }

    return threadHtml;
  };

  const getReplyContentWithThread = (emails: ThreadEmail[]) => `
    <div><br/></div>

    <!-- INICIO_HILO_HISTORICO -->
    <div><br/></div>
    ${buildThreadHistory(emails)}
    <!-- FIN_HILO_HISTORICO -->
`;

  const handleReply = async () => {
    const from = String(history.emailFrom);
    removeDraft(Channels.EMAIL);

    if (isOpen) {
      setIsOpen(false);
      // wait for the next tick for the editor to close
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    setChannel(Channels.EMAIL);
    setHistoryIdToReply(history.id);
    setTo([{ email: from, id: from }]);
    setSubject(history.emailSubject ?? '');


    let threadData: ThreadEmail[] = [];
    if (history.emailId) {
      threadData = await fetchEmailThread(history.emailId);
    }

    const currentEmail: ThreadEmail = {
      id: history.id,
      from: history.emailFrom ?? '',
      subject: history.emailSubject ?? '',
      original_content: history.emailOriginalContent ?? '',
      original_text: history.emailOriginalText ?? '',
      content: history.emailContent ?? '',
      created_at: history.createdAt,
    };

    const replyContent = getReplyContentWithThread([currentEmail, ...threadData]);
    setEditorContent(replyContent);
    setIsOpen(true);
  };

  const handleReplyAll = async () => {
    const from = String(history.emailFrom);
    removeDraft(Channels.EMAIL);
    const toEmails = history.emailTo.map((email) => ({ email, id: email })) ?? [];
    const copyEmails = history.emailCC?.map((email) => ({ email, id: email })) ?? [];
    const finalCc = [...copyEmails, ...toEmails];

    if (isOpen) {
      setIsOpen(false);
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    setChannel(Channels.EMAIL);
    setHistoryIdToReply(history.id);
    setTo([{ email: from, id: from }]);
    setSubject(history.emailSubject ?? '');

    let threadData: ThreadEmail[] = [];
    if (history.emailId) {
      threadData = await fetchEmailThread(history.emailId);
    }

    const replyContent = getReplyContentWithThread(threadData);
    setEditorContent(replyContent);

    if (finalCc.length > 0) {
      setCc(finalCc);
      onToggleCc();
    }
    setIsOpen(true);
  };

  const isLegacyEmail = !history.emailOriginalContent && !history.emailOriginalText;
  const hasQuotedContent = history.emailContent &&
    (history.emailContent.includes('escribió:') ||
      history.emailContent.includes('<hr') ||
      history.emailContent.length > (history.emailOriginalContent?.length || 0) * 2);

  const isPartOfThread = !!history.emailInReplyOfEmailId ||
    (history.emailContent && history.emailContent !== history.emailOriginalContent);

  return (
    <Box flexDirection={isCurrentUser ? 'row-reverse' : 'row'} gap={1} display="flex">
      <Stack>
        <Paper
          sx={{
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            width: '100%',
          }}
        >
          <Stack
            sx={{
              p: (theme) => theme.spacing(2),
              gap: (theme) => theme.spacing(1),
            }}
          >
            <Stack display="flex" flexDirection="row" gap={4} pb={1} justifyContent="space-between">
              <Stack gap={1} display="flex">
                <HeaderRow label={t('cases.view.content.from')} content={history.emailFrom} />
                <HeaderRow label={t('cases.view.content.to')} content={destinataries} />
                {cc && <HeaderRow label={t('cases.view.content.cc')} content={cc} />}
                {bcc && <HeaderRow label={t('cases.view.content.bcc')} content={bcc} />}

                <HeaderRow label={t('cases.view.content.subject')} content={subject} />
                <HeaderRow label={t('cases.view.content.date')} content={createdAtDate} />
              </Stack>
              <Box>
                <Box sx={{ width: 'fit-content' }}>
                  <IconButton onClick={handleReplyAll} aria-label={t('cases.view.content.replyToAll')} size="small">
                    <ReplyAllRounded color="disabled" />
                  </IconButton>
                  <IconButton onClick={handleReply} aria-label={t('cases.view.content.reply')} size="small">
                    <ReplyRounded color="disabled" />
                  </IconButton>
                </Box>
              </Box>
            </Stack>
            <Box sx={{ width: '100%' }}>
              <Divider />
            </Box>
            <Stack>
              <Stack sx={{ width: '100%', pt: (theme) => theme.spacing(1) }}>
                <ContentStack>
                  <EditorContainer
                    sx={{ maxWidth: 750, maxHeight: 500, overflow: 'auto' }}
                    onClick={(e) => {
                      handleEditorClickEvent(e.target as HTMLElement);
                    }}
                    dangerouslySetInnerHTML={{ __html: String(content) }}
                  />
                  <AttachmentsList files={filesNotEmbedded(history.files, history)} />
                </ContentStack>
              </Stack>
            </Stack>
            <Box display="flex" gap={1} mt={1}>
              {isLegacyEmail && (
                <Tooltip title={t('cases.view.content.legacyEmailTooltip')}>
                  <Chip
                    size="small"
                    label="Legacy"
                    color="secondary"
                    variant="outlined"
                  />
                </Tooltip>
              )}

              {hasQuotedContent && !isLegacyEmail && (
                <Tooltip title={t('cases.view.content.cleanedEmailTooltip')}>
                  <Chip
                    size="small"
                    label="Cleaned"
                    color="success"
                    variant="outlined"
                    icon={<SortRounded />}
                  />
                </Tooltip>
              )}
            </Box>

            {canShowFullEmail && (
              <Box mt={1}>
                <ThreadToggleButton
                  size="small"
                  variant="text"
                  onClick={() => setShowFullEmail((value) => !value)}
                  startIcon={showFullEmail ? <UnfoldLessRounded /> : <UnfoldMoreRounded />}
                >
                  {showFullEmail
                    ? t('cases.view.content.showCleanedEmail')
                    : t('cases.view.content.showFullEmail')}
                </ThreadToggleButton>
              </Box>
            )}

            {history.emailId && !isLegacyEmail && (
              <EmailThread
                emailId={history.emailId}
                isPartOfThread={!!isPartOfThread}
              />
            )}
          </Stack>
        </Paper>
        <Typography textAlign="right" pt={1} color="text.disabled" variant="subtitle2">
          {createdAtTime}
        </Typography>
      </Stack>
    </Box>
  );
};
